import { useState, useMemo } from 'react';
import { CoachLayout } from '@/components/coach/layout/CoachLayout';
import { sportConfig, segmentTypeConfig } from '@/lib/coachConstants';
import type { SportType } from '@/lib/coachConstants';
import { useWorkoutTemplates, useDuplicateWorkoutTemplate } from '@/hooks/useWorkoutTemplates';
import { useCoachStore } from '@/stores/coachStore';
import { useAddCalendarEntry } from '@/hooks/usePlanInstances';
import type { WorkoutTemplateResponse } from '@/lib/templateApi';
import { cn } from '@/lib/utils';
import { Search, Plus, Copy, Pencil, ListPlus, Clock, Dumbbell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const stimulusOptions = ['All', 'Endurance', 'Threshold', 'VO2max', 'Recovery', 'Strength-Endurance', 'Conditioning'];
const difficultyOptions = ['All', 'beginner', 'intermediate', 'advanced', 'elite'];
const sportOptions: ('All' | SportType)[] = ['All', 'running', 'cycling', 'swimming', 'strength', 'crossfit', 'hyrox', 'multi-sport'];

function WorkoutCard({ template, onDuplicate, onAddToPlan }: { template: WorkoutTemplateResponse; onDuplicate: () => void; onAddToPlan: () => void }) {
  const navigate = useNavigate();
  const sport = sportConfig[template.sport as SportType];

  return (
    <div className="rounded-lg border border-border/15 bg-card/30 hover:bg-card/50 transition-all group">
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-medium text-foreground truncate">{template.name}</h3>
            <div className="flex items-center gap-2 mt-1">
              {sport && (
                <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: `${sport.color}15`, color: sport.color }}>
                  {sport.label}
                </span>
              )}
              <span className="text-[10px] text-muted-foreground">{template.stimulus}</span>
            </div>
          </div>
          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" onClick={() => navigate('/coach/workouts')} title="Edit">
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" title="Duplicate" onClick={onDuplicate}>
              <Copy className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-accent" title="Add to Plan" onClick={onAddToPlan}>
              <ListPlus className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
          <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {template.duration}</span>
          <span className="flex items-center gap-1"><Dumbbell className="h-3 w-3" /> {template.segments.length} segments</span>
          <span className="capitalize">{template.difficulty}</span>
        </div>
        <div className="mt-3 flex gap-1">
          {template.segments.map((seg) => {
            const segType = seg.type ?? 'main';
            const color = segmentTypeConfig[segType as keyof typeof segmentTypeConfig]?.color ?? 'hsl(200 65% 55%)';
            return (
              <div key={seg.id} className="h-1.5 rounded-full flex-1" style={{ background: color, opacity: 0.7 }} title={`${seg.name} (${seg.type})`} />
            );
          })}
        </div>
        {template.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3">
            {template.tags.slice(0, 4).map((tag) => (
              <span key={tag} className="text-[8px] font-mono px-1.5 py-0.5 rounded bg-border/15 text-muted-foreground">{tag}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function WorkoutLibraryPage() {
  const { data, isLoading } = useWorkoutTemplates();
  const templates = data?.items ?? [];
  const duplicateMutation = useDuplicateWorkoutTemplate();

  const { selectedPlanInstanceId } = useCoachStore();
  const addCalendarEntry = useAddCalendarEntry(selectedPlanInstanceId ?? '');

  const [search, setSearch] = useState('');
  const [sportFilter, setSportFilter] = useState<'All' | SportType>('All');
  const [stimulusFilter, setStimulusFilter] = useState('All');
  const [difficultyFilter, setDifficultyFilter] = useState('All');

  const filtered = useMemo(() => {
    return templates.filter((t) => {
      if (search && !t.name.toLowerCase().includes(search.toLowerCase()) && !t.tags.some((tag) => tag.toLowerCase().includes(search.toLowerCase()))) return false;
      if (sportFilter !== 'All' && t.sport !== sportFilter) return false;
      if (stimulusFilter !== 'All' && t.stimulus !== stimulusFilter) return false;
      if (difficultyFilter !== 'All' && t.difficulty !== difficultyFilter) return false;
      return true;
    });
  }, [templates, search, sportFilter, stimulusFilter, difficultyFilter]);

  const handleDuplicate = async (templateId: string) => {
    try {
      const copy = await duplicateMutation.mutateAsync(templateId);
      toast.success(`Duplicated as "${copy.name}"`);
    } catch {
      toast.error('Failed to duplicate');
    }
  };

  const handleAddToPlan = async (template: WorkoutTemplateResponse) => {
    if (!selectedPlanInstanceId) {
      toast.error('No plan instance selected. Select a plan in Plan Builder first.');
      return;
    }
    const today = new Date().toISOString().split('T')[0];
    try {
      await addCalendarEntry.mutateAsync({ workout_template_id: template.id, scheduled_date: today });
      toast.success(`"${template.name}" added to calendar`, { description: 'View in Plan Calendar' });
    } catch {
      toast.error('Failed to add workout to calendar');
    }
  };

  if (isLoading) {
    return (
      <CoachLayout>
        <div className="flex items-center justify-center h-48">
          <p className="text-xs text-muted-foreground font-mono">Loading templates...</p>
        </div>
      </CoachLayout>
    );
  }

  return (
    <CoachLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-foreground">Template Library</h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              Reusable workout templates across all sports
              {selectedPlanInstanceId && <span className="text-accent"> · Adding to active plan</span>}
            </p>
          </div>
          <Button size="sm" className="gap-1.5 bg-accent/90 hover:bg-accent text-accent-foreground text-xs font-mono uppercase tracking-wider" onClick={() => toast.info('Use Workout Builder to create new templates')}>
            <Plus className="h-3.5 w-3.5" /> New Template
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search workouts..."
              className="w-full pl-9 pr-3 py-2 text-xs bg-card/30 border border-border/20 rounded-lg text-foreground placeholder:text-muted-foreground/50 focus:border-accent/40 focus:outline-none transition-colors" />
          </div>
          <div className="flex flex-wrap gap-2">
            <select value={sportFilter} onChange={(e) => setSportFilter(e.target.value as 'All' | SportType)} className="text-[10px] font-mono uppercase tracking-wider bg-card/30 border border-border/20 rounded px-2 py-1.5 text-foreground focus:border-accent/40 focus:outline-none">
              {sportOptions.map((s) => <option key={s} value={s}>{s === 'All' ? 'All Sports' : sportConfig[s as SportType].label}</option>)}
            </select>
            <select value={stimulusFilter} onChange={(e) => setStimulusFilter(e.target.value)} className="text-[10px] font-mono uppercase tracking-wider bg-card/30 border border-border/20 rounded px-2 py-1.5 text-foreground focus:border-accent/40 focus:outline-none">
              {stimulusOptions.map((s) => <option key={s} value={s}>{s === 'All' ? 'All Stimulus' : s}</option>)}
            </select>
            <select value={difficultyFilter} onChange={(e) => setDifficultyFilter(e.target.value)} className="text-[10px] font-mono uppercase tracking-wider bg-card/30 border border-border/20 rounded px-2 py-1.5 text-foreground focus:border-accent/40 focus:outline-none">
              {difficultyOptions.map((d) => <option key={d} value={d}>{d === 'All' ? 'All Levels' : d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
            </select>
          </div>
        </div>

        <div className="text-[10px] font-mono text-muted-foreground">{filtered.length} template{filtered.length !== 1 ? 's' : ''}</div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((t) => (
            <WorkoutCard key={t.id} template={t} onDuplicate={() => handleDuplicate(t.id)} onAddToPlan={() => handleAddToPlan(t)} />
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="rounded-lg border border-border/15 bg-card/20 p-12 text-center">
            <p className="text-sm text-muted-foreground">No templates match your filters</p>
          </div>
        )}
      </div>
    </CoachLayout>
  );
}
